<script lang="ts">
	import type { Snippet } from 'svelte';
	import LoadingSpinner from './LoadingSpinner.svelte';

	interface Props {
		/** contained = primary, outlined = secondary, text = quiet, link = quiet in the link colour, danger = destructive. */
		variant?: 'contained' | 'outlined' | 'text' | 'link' | 'danger';
		size?: 'xs' | 'sm' | 'md';
		disabled?: boolean;
		/** Shows a spinner and marks the button busy. */
		loading?: boolean;
		/** Squares the edge joined to a neighbouring control, e.g. an input before it (`start`). */
		attached?: 'start' | 'end';
		children: Snippet;
		onclick?: () => void;
		'aria-label'?: string;
		/** Native hover tooltip; useful for icon-only text-variant buttons. */
		title?: string;
		/** Renders a button-styled link instead of a `<button>`; callers resolve app routes. */
		href?: string;
		target?: string;
		'aria-expanded'?: boolean;
		type?: 'button' | 'submit';
		/** Associates a submit button outside the form (e.g. in a Modal footer) by form id. */
		form?: string;
		/** Invokes a popover by id (Popover API). */
		popovertarget?: string;
		/** CSS anchor name, so an anchored popover can position against this button. */
		anchorName?: string;
	}

	const {
		variant = 'contained',
		size = 'md',
		loading = false,
		attached,
		disabled = false,
		children,
		onclick,
		'aria-label': ariaLabel,
		title,
		href,
		target,
		'aria-expanded': ariaExpanded,
		type = 'button',
		form,
		popovertarget,
		anchorName
	}: Props = $props();

	const classes = $derived(
		['button', variant, size, attached && (attached === 'start' ? 'attachedStart' : 'attachedEnd')]
			.filter(Boolean)
			.join(' ')
	);
</script>

{#snippet content()}
	{@render children()}
{/snippet}

{#if href}
	<a
		class={classes}
		{href}
		{target}
		rel={target === '_blank' ? 'noopener' : undefined}
		{onclick}
		{title}
		aria-label={ariaLabel}>
		{@render content()}
	</a>
{:else}
	<button
		{type}
		{form}
		class={classes}
		{disabled}
		{onclick}
		{title}
		aria-label={ariaLabel}
		aria-expanded={ariaExpanded}
		aria-busy={loading || undefined}
		{popovertarget}
		style:anchor-name={anchorName}>
		{@render content()}
		{#if loading}
			<LoadingSpinner size="sm" tone="current" decorative />
		{/if}
	</button>
{/if}

<style>
	.button {
		--button-text: var(--accent-contrast);
		--button-surface: var(--accent);
		--button-border: var(--accent);
		display: inline-flex;
		justify-content: center;
		align-items: center;
		gap: var(--space-2);
		transition:
			background-color 0.15s ease-out,
			border-color 0.15s ease-out,
			color 0.15s ease-out;
		cursor: pointer;
		border: 1px solid var(--button-border);
		border-radius: var(--radius);
		background-color: var(--button-surface);
		padding-inline: var(--space-5);
		min-height: var(--height-button);
		color: var(--button-text);
		font-weight: var(--weight-semibold);
		font-size: var(--font-size-base);
		line-height: 1.25;
		text-decoration: none;
		user-select: none;

		&.contained:hover:not(:disabled) {
			--button-surface: var(--accent-hover);
			--button-border: var(--accent-hover);
		}

		&.contained:active:not(:disabled) {
			--button-surface: var(--accent-active);
			--button-border: var(--accent-active);
		}

		&.outlined {
			--button-text: var(--text-color);
			--button-surface: var(--surface);
			--button-border: var(--border-strong);

			&:hover:not(:disabled) {
				--button-surface: var(--hover-surface);
			}

			&:active:not(:disabled) {
				--button-surface: var(--active-surface);
			}
		}

		&.text {
			--button-text: var(--text-color);
			--button-surface: transparent;
			--button-border: transparent;
			padding-inline: var(--space-3);
			min-width: var(--target-min);

			&:hover:not(:disabled) {
				--button-surface: var(--hover-surface);
			}

			&:active:not(:disabled) {
				--button-surface: var(--active-surface);
			}
		}

		&.link {
			--button-text: var(--link-color);
			--button-surface: transparent;
			--button-border: transparent;
			padding-inline: var(--space-3);
			min-width: var(--target-min);

			&:hover:not(:disabled) {
				--button-surface: var(--hover-surface);
			}

			&:active:not(:disabled) {
				--button-surface: var(--active-surface);
			}
		}

		&.attachedStart {
			border-start-start-radius: 0;
			border-end-start-radius: 0;
		}

		&.attachedEnd {
			border-start-end-radius: 0;
			border-end-end-radius: 0;
		}

		&.danger {
			--button-text: var(--status-danger);
			--button-surface: var(--surface);
			--button-border: var(--status-danger);

			&:hover:not(:disabled) {
				--button-surface: var(--status-danger-surface);
			}

			&:active:not(:disabled) {
				--button-surface: color-mix(
					in srgb,
					var(--status-danger-surface),
					var(--status-danger) 15%
				);
			}
		}

		&.sm {
			padding-inline: var(--space-3);
			min-height: var(--height-button-sm);
			font-size: var(--font-size-sm);
		}

		&.xs {
			padding-inline: var(--space-2);
			min-height: var(--target-min);
			font-size: var(--font-size-sm);
		}

		&:disabled {
			--button-text: var(--disabled-text);
			--button-surface: var(--disabled-surface);
			--button-border: var(--disabled-surface);
			cursor: not-allowed;

			&.text,
			&.link {
				--button-surface: transparent;
				--button-border: transparent;
			}
		}

		> :global(svg) {
			flex-shrink: 0;
			/* The global `max-width: 100%` lets a squeezed table shrink the icon below its size. */
			max-width: none;
			width: var(--icon-md);
			height: var(--icon-md);
		}
	}
</style>
